import React from "react";
import { useSelector } from "react-redux";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import {
  FieldHeader,
  GenreDropdown,
  MultiLineTextField,
  NumericInputField,
  SectionTitle,
  SeparatorBar,
  TextField,
} from "../../../components";
import * as commonStyles from "../../../components/commonStyles/commonStyles.module.css";
import { DynamicTextListField } from "../../../components/DynamicTextListField/DynamicTextListField";
import { FormattedText } from "../../../components/FormattedText";
import { FormCard } from "../../../components/FormCard";
import { RightSideWrapper } from "../../../components/RightSideWrapper";
import { SubForm, SubFormNavigationProps } from "../../../components/SubForm";
import {
  gameConceptInputsMaxLength,
  gameInfoConceptInitialValues,
  useIdXboxGameInformationConceptValidationSchema,
} from "../../../helpers/formValidation";
import { useStrings } from "../../../localization/localizationHelpers";
import { getIsSavingForm } from "../../../redux/selectors";
import { GameInformationFormId, GameInformationSubFormsId } from "../GameInformationPageConstants";
import * as styles from "./ConceptDetailsSubForm.module.css";

const ConceptDetailsSubForm = (props: SubFormNavigationProps) => {
  const [
    basicInfo,
    describeTheGameYouAreApplyingFor,
    gameTitle,
    gameTitleSubtitle,
    codeName,
    gameDeveloper,
    gameDeveloperSubtitle,
    theHook,
    theHookSubtitle,
    gameplayVideo,
    gameplayVideoSubtitle,
    gameplayDetailsAndSummary,
    gameplayDetailsAndSummarySubtitle,
    letUsExperienceTheMagic,
    letUsExperienceTheMagicSubtitle,
    gameBuildLinks,
    gameBuildLinksSubtitle,
    gameBuildKeys,
    gameBuildKeysSubtitle,
    additionalLinks,
    additionalLinksSubtitle,
    addLink,
    addDeveloper,
    addKey,
    examplelink,
    gameplayDuration,
    gameplayDurationSubtitle,
    levelsDetails,
    levelsDetailsSubtitle,
    levelsNumber,
    levelsNumberSubtitle,
    steamLink,
    steamLinkSubtitle,
    itchIoLink,
    itchIoLinkSubtitle,
  ] = useStrings(
    "basicInfo",
    "describeTheGameYouAreApplyingFor",
    "gameTitle",
    "gameTitleSubtitle",
    "codeName",
    "gameDeveloper",
    "gameDeveloperSubtitle",
    "theHook",
    "theHookSubtitle",
    "gameplayVideo",
    "gameplayVideoSubtitle",
    "gameplayDetailsAndSummary",
    "gameplayDetailsAndSummarySubtitle",
    "letUsExperienceTheMagic",
    "letUsExperienceTheMagicSubtitle",
    "gameBuildLinks",
    "gameBuildLinksSubtitle",
    "gameBuildKeys",
    "gameBuildKeysSubtitle",
    "additionalLinks",
    "additionalLinksSubtitle",
    "addLink",
    "addDeveloper",
    "addKey",
    "examplelink",
    "gameplayDuration",
    "gameplayDurationSubtitle",
    "levelsDetails",
    "levelsDetailsSubtitle",
    "levelsNumber",
    "levelsNumberSubtitle",
    "steamLink",
    "steamLinkSubtitle",
    "itchIoLink",
    "itchIoLinkSubtitle"
  );
  const validationSchema = useIdXboxGameInformationConceptValidationSchema();
  const isSavingFormToCrm = useSelector(getIsSavingForm);
  return (
    <SubForm
      validateConfig={validationSchema}
      initialValues={gameInfoConceptInitialValues}
      formId={GameInformationFormId}
      subFormId={GameInformationSubFormsId.ConceptDetails}
      {...props}
    >
      <SectionTitle title={basicInfo} subtitle={describeTheGameYouAreApplyingFor} />
      <Row className={styles.container}>
        <Column>
          <TextField
            name="gameTitle"
            title={gameTitle}
            subtitle={gameTitleSubtitle}
            isRequiredField={true}
            isSaving={isSavingFormToCrm}
            maxLengthsByFieldName={gameConceptInputsMaxLength}
          />
        </Column>
      </Row>
      <Row className={styles.container}>
        <Column>
          <TextField name="codeName" title={codeName} isSaving={isSavingFormToCrm} />
        </Column>
      </Row>
      <RightSideWrapper
        rightSideComponent={<FormCard cardId={GameInformationSubFormsId.ConceptDetails} />}
      >
        <Row className={styles.container}>
          <Column>
            <GenreDropdown />
          </Column>
          <Column />
        </Row>
      </RightSideWrapper>
      <Row className={styles.dynamicContainer}>
        <Column>
          <DynamicTextListField
            title={gameDeveloper}
            subtitle={gameDeveloperSubtitle}
            name="gameDevelopers"
            buttonLabel={addDeveloper}
            isSaving={isSavingFormToCrm}
            ariaLabel={gameDeveloper}
          />
        </Column>
      </Row>
      <SeparatorBar />
      <MultiLineTextField
        name="hook"
        title={theHook}
        subtitle={theHookSubtitle}
        isRequiredField={true}
        isSaving={isSavingFormToCrm}
        maxLengthsByFieldName={gameConceptInputsMaxLength}
      />
      <div className={styles.dynamicContainer}>
        <DynamicTextListField
          title={gameplayVideo}
          subtitle={gameplayVideoSubtitle}
          name="gameplayVideosLinks"
          buttonLabel={addLink}
          placeholder={examplelink}
          isRequiredField={true}
          isSaving={isSavingFormToCrm}
          ariaLabel={gameplayVideo}
        />
      </div>
      <MultiLineTextField
        name="gameplayDetails"
        title={gameplayDetailsAndSummary}
        subtitle={gameplayDetailsAndSummarySubtitle}
        isRequiredField={true}
        isSaving={isSavingFormToCrm}
        largeTextBox={true}
        maxLengthsByFieldName={gameConceptInputsMaxLength}
      />
      <TextField
        name="gameplayDuration"
        title={gameplayDuration}
        subtitle={gameplayDurationSubtitle}
        isRequiredField={true}
        isSaving={isSavingFormToCrm}
        maxLengthsByFieldName={gameConceptInputsMaxLength}
      />
      <MultiLineTextField
        name="levelsDetails"
        title={levelsDetails}
        subtitle={levelsDetailsSubtitle}
        isRequiredField={true}
        isSaving={isSavingFormToCrm}
        maxLengthsByFieldName={gameConceptInputsMaxLength}
      />
      <NumericInputField
        name="levelsNumber"
        title={levelsNumber}
        subtitle={levelsNumberSubtitle}
        minValue={1}
        maxValue={1000}
        isSaving={isSavingFormToCrm}
      />
      <SeparatorBar />
      <SectionTitle title={letUsExperienceTheMagic} subtitle={letUsExperienceTheMagicSubtitle} />
      <div className={styles.dynamicContainer}>
        <DynamicTextListField
          name="gameBuildLinks"
          buttonLabel={addLink}
          placeholder={examplelink}
          isSaving={isSavingFormToCrm}
          isRequiredField={true}
          ariaLabel={gameBuildLinks}
          customHeader={
            <>
              <FieldHeader title={gameBuildLinks} isRequiredField={true}></FieldHeader>
              <FormattedText text={gameBuildLinksSubtitle} className={commonStyles.fieldSubtitle} />
            </>
          }
        />
      </div>
      <TextField
        name="steamLink"
        title={steamLink}
        subtitle={steamLinkSubtitle}
        isSaving={isSavingFormToCrm}
      />
      <TextField
        name="itchIoLink"
        title={itchIoLink}
        subtitle={itchIoLinkSubtitle}
        isSaving={isSavingFormToCrm}
      />
      <div className={styles.dynamicContainer}>
        <DynamicTextListField
          name="gameBuildKeys"
          title={gameBuildKeys}
          subtitle={gameBuildKeysSubtitle}
          buttonLabel={addKey}
          isSaving={isSavingFormToCrm}
          ariaLabel={gameBuildKeys}
        />
        <DynamicTextListField
          name="additionalLinks"
          title={additionalLinks}
          subtitle={additionalLinksSubtitle}
          buttonLabel={addLink}
          placeholder={examplelink}
          isSaving={isSavingFormToCrm}
          ariaLabel={additionalLinks}
        />
      </div>
    </SubForm>
  );
};

export default ConceptDetailsSubForm;
